import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { KeyboardArrowDownOutlined, KeyboardArrowRightOutlined } from '@mui/icons-material';
import { PRIMARY } from '../constants';
import { PxInput, UnitInput, StyleInput, ColorInput } from './primitives';

// ── SpacingControl ────────────────────────────────────────────────────────────
// Collapsed: one "all sides" input. Expanded: T/R/B/L individually.
export function SpacingControl({ label, style, onChange, type = 'padding' }) {
  const [expanded, setExpanded] = useState(false);

  const parsePadding = (val) => {
    if (!val) return ['', '', '', ''];
    const parts = val.trim().split(/\s+/);
    if (parts.length === 1) return [parts[0], parts[0], parts[0], parts[0]];
    if (parts.length === 2) return [parts[0], parts[1], parts[0], parts[1]];
    if (parts.length === 3) return [parts[0], parts[1], parts[2], parts[1]];
    return parts.slice(0, 4);
  };

  const stripPx = v => (v ?? '').replace('px', '').trim();
  const addPx   = v => { const n = parseFloat(v); return isNaN(n) ? '' : n + 'px'; };

  if (type === 'padding') {
    const [t, r, b, l] = parsePadding(style.padding || '');
    const allSame = t === r && r === b && b === l;

    const setAll  = v => onChange('padding', [addPx(v), addPx(v), addPx(v), addPx(v)].join(' '));
    const setSide = (idx, v) => {
      const sides = [t, r, b, l];
      sides[idx] = addPx(v);
      onChange('padding', sides.join(' '));
    };

    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: expanded ? 0.75 : 0 }}>
          <Box onClick={() => setExpanded(v => !v)} sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: expanded ? PRIMARY : '#9ea8b3', flexShrink: 0, transition: 'color 0.1s' }}>
            {expanded ? <KeyboardArrowDownOutlined sx={{ fontSize: 14 }} /> : <KeyboardArrowRightOutlined sx={{ fontSize: 14 }} />}
          </Box>
          <Typography sx={{ fontSize: '0.7rem', color: '#7a8494', width: 48, flexShrink: 0 }}>{label}</Typography>
          {!expanded && (
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <PxInput value={allSame ? stripPx(t) : ''} onChange={setAll} placeholder="0" />
            </Box>
          )}
        </Box>
        {expanded && (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5, pl: 1.5 }}>
            {[['Top', t, 0], ['Right', r, 1], ['Bottom', b, 2], ['Left', l, 3]].map(([lbl, val, idx]) => (
              <Box key={lbl} sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.65rem', color: '#9ea8b3', mb: 0.25 }}>{lbl}</Typography>
                <PxInput value={stripPx(val)} onChange={v => setSide(idx, v)} placeholder="0" />
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  }

  // Margin
  const mT = stripPx(style.marginTop    || '');
  const mR = stripPx(style.marginRight  || '');
  const mB = stripPx(style.marginBottom || '');
  const mL = stripPx(style.marginLeft   || '');
  const allSameM = mT === mR && mR === mB && mB === mL;

  const setAllM  = v => { ['marginTop','marginRight','marginBottom','marginLeft'].forEach(k => onChange(k, addPx(v))); };
  const setSideM = (key, v) => onChange(key, addPx(v));

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: expanded ? 0.75 : 0 }}>
        <Box onClick={() => setExpanded(v => !v)} sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: expanded ? PRIMARY : '#9ea8b3', flexShrink: 0, transition: 'color 0.1s' }}>
          {expanded ? <KeyboardArrowDownOutlined sx={{ fontSize: 14 }} /> : <KeyboardArrowRightOutlined sx={{ fontSize: 14 }} />}
        </Box>
        <Typography sx={{ fontSize: '0.7rem', color: '#7a8494', width: 48, flexShrink: 0 }}>{label}</Typography>
        {!expanded && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <PxInput value={allSameM ? mT : ''} onChange={setAllM} placeholder="0" />
          </Box>
        )}
      </Box>
      {expanded && (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5, pl: 1.5 }}>
          {[['Top','marginTop',mT],['Right','marginRight',mR],['Bottom','marginBottom',mB],['Left','marginLeft',mL]].map(([lbl,key,val]) => (
            <Box key={key} sx={{ minWidth: 0, overflow: 'hidden' }}>
              <Typography sx={{ fontSize: '0.65rem', color: '#9ea8b3', mb: 0.25 }}>{lbl}</Typography>
              <PxInput value={val} onChange={v => setSideM(key, v)} placeholder="0" />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ── RadiusControl ─────────────────────────────────────────────────────────────
export function RadiusControl({ style, onChange }) {
  const [expanded, setExpanded] = useState(false);

  const parseRadius = (val) => {
    if (!val) return ['', '', '', ''];
    const parts = val.trim().split(/\s+/);
    if (parts.length === 1) return [parts[0], parts[0], parts[0], parts[0]];
    if (parts.length === 2) return [parts[0], parts[1], parts[0], parts[1]];
    if (parts.length === 3) return [parts[0], parts[1], parts[2], parts[1]];
    return parts.slice(0, 4);
  };
  const stripPx = v => (v ?? '').replace('px', '').trim();
  const addPx   = v => { const n = parseFloat(v); return isNaN(n) ? '0px' : n + 'px'; };

  const raw = style.borderRadius || '';
  const [tl, tr, br, bl] = parseRadius(raw);
  const allSame = tl === tr && tr === br && br === bl;

  const setAll  = v => onChange('borderRadius', addPx(v));
  const setSide = (idx, v) => {
    const sides = [tl, tr, br, bl];
    sides[idx] = addPx(v);
    onChange('borderRadius', sides.join(' '));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: expanded ? 0.75 : 0 }}>
        <Box onClick={() => setExpanded(v => !v)} sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: expanded ? PRIMARY : '#9ea8b3', flexShrink: 0, transition: 'color 0.1s' }}>
          {expanded ? <KeyboardArrowDownOutlined sx={{ fontSize: 14 }} /> : <KeyboardArrowRightOutlined sx={{ fontSize: 14 }} />}
        </Box>
        <Typography sx={{ fontSize: '0.7rem', color: '#7a8494', width: 48, flexShrink: 0 }}>Radius</Typography>
        {!expanded && <Box sx={{ flex: 1, minWidth: 0 }}><PxInput value={allSame ? stripPx(tl) : ''} onChange={setAll} placeholder="0" /></Box>}
      </Box>
      {expanded && (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5, pl: 1.5 }}>
          {[['TL', tl, 0], ['TR', tr, 1], ['BR', br, 2], ['BL', bl, 3]].map(([lbl, val, idx]) => (
            <Box key={lbl} sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.65rem', color: '#9ea8b3', mb: 0.25 }}>{lbl}</Typography>
              <PxInput value={stripPx(val)} onChange={v => setSide(idx, v)} placeholder="0" />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ── BorderControl ─────────────────────────────────────────────────────────────
export function BorderControl({ style, onChange }) {
  const [expanded, setExpanded] = useState(false);

  const collapsed = style.border || '';
  const SIDES = [
    ['Top',    'borderTop',    style.borderTop    || ''],
    ['Right',  'borderRight',  style.borderRight  || ''],
    ['Bottom', 'borderBottom', style.borderBottom || ''],
    ['Left',   'borderLeft',   style.borderLeft   || ''],
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: expanded ? 0.75 : 0 }}>
        <Box onClick={() => setExpanded(v => !v)} sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: expanded ? PRIMARY : '#9ea8b3', flexShrink: 0, transition: 'color 0.1s' }}>
          {expanded ? <KeyboardArrowDownOutlined sx={{ fontSize: 14 }} /> : <KeyboardArrowRightOutlined sx={{ fontSize: 14 }} />}
        </Box>
        <Typography sx={{ fontSize: '0.7rem', color: '#7a8494', width: 48, flexShrink: 0 }}>Border</Typography>
        {!expanded && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <StyleInput value={collapsed} onChange={v => onChange('border', v)} placeholder="1px solid #eee" />
          </Box>
        )}
      </Box>
      {expanded && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, pl: 1.5 }}>
          {SIDES.map(([lbl, key, val]) => (
            <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '0.65rem', color: '#9ea8b3', width: 36, flexShrink: 0 }}>{lbl}</Typography>
              <Box sx={{ flex: 1, minWidth: 0 }}><StyleInput value={val} onChange={v => onChange(key, v)} placeholder="1px solid #eee" /></Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ── ShadowControl ─────────────────────────────────────────────────────────────
export function ShadowControl({ label = 'Shadow', value, onChange }) {
  const [expanded, setExpanded] = useState(false);

  const parse = (str) => {
    if (!str) return { x: 0, y: 4, blur: 12, spread: 0, color: 'rgba(0,0,0,0.1)', inset: false };
    const inset = str.includes('inset');
    const s = str.replace('inset', '').trim();
    const colorMatch = s.match(/rgba?\([^)]+\)|#[0-9a-fA-F]{3,8}/);
    const color = colorMatch ? colorMatch[0] : 'rgba(0,0,0,0.1)';
    const nums = s.replace(color, '').trim().split(/\s+/).map(parseFloat).filter(n => !isNaN(n));
    return { x: nums[0] ?? 0, y: nums[1] ?? 4, blur: nums[2] ?? 12, spread: nums[3] ?? 0, color, inset };
  };

  const compile = ({ x, y, blur, spread, color, inset }) =>
    `${inset ? 'inset ' : ''}${x}px ${y}px ${blur}px ${spread}px ${color}`;

  const p = parse(value);
  const update = (patch) => onChange(compile({ ...p, ...patch }));

  const SLIDERS = [
    ['X', 'x', -50, 50, 1, p.x], ['Y', 'y', -50, 50, 1, p.y],
    ['Blur', 'blur', 0, 100, 1, p.blur], ['Spread', 'spread', -30, 30, 1, p.spread],
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: expanded ? 0.75 : 0 }}>
        <Box onClick={() => setExpanded(v => !v)} sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: expanded ? PRIMARY : '#9ea8b3', flexShrink: 0, transition: 'color 0.1s' }}>
          {expanded ? <KeyboardArrowDownOutlined sx={{ fontSize: 14 }} /> : <KeyboardArrowRightOutlined sx={{ fontSize: 14 }} />}
        </Box>
        <Typography sx={{ fontSize: '0.7rem', color: '#7a8494', width: 48, flexShrink: 0 }}>{label}</Typography>
        {!expanded && <Box sx={{ flex: 1, minWidth: 0 }}><StyleInput value={value || ''} onChange={onChange} placeholder="0 4px 12px rgba(0,0,0,0.1)" /></Box>}
      </Box>
      {expanded && (
        <Box sx={{ pl: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {SLIDERS.map(([lbl, key, min, max, step, val]) => (
            <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '0.65rem', color: '#9ea8b3', width: 36, flexShrink: 0 }}>{lbl}</Typography>
              <input type="range" min={min} max={max} step={step} value={val}
                onChange={e => update({ [key]: parseFloat(e.target.value) })}
                style={{ flex: 1, cursor: 'pointer', accentColor: PRIMARY }} />
              <Typography sx={{ fontSize: '0.65rem', color: '#5a6474', width: 24, textAlign: 'right', flexShrink: 0 }}>{val}</Typography>
            </Box>
          ))}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '0.65rem', color: '#9ea8b3', width: 36, flexShrink: 0 }}>Color</Typography>
            <Box sx={{ flex: 1, minWidth: 0 }}><ColorInput value={p.color.startsWith('#') ? p.color : '#000000'} onChange={v => update({ color: v })} /></Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '0.65rem', color: '#9ea8b3', width: 36, flexShrink: 0 }}>Inset</Typography>
            <Box onClick={() => update({ inset: !p.inset })} sx={{ width: 32, height: 18, borderRadius: 9, background: p.inset ? PRIMARY : '#d0d7e2', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
              <Box sx={{ position: 'absolute', top: 2, left: p.inset ? 14 : 2, width: 14, height: 14, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '0.65rem', color: '#9ea8b3', width: 36, flexShrink: 0 }}>Raw</Typography>
            <Box sx={{ flex: 1, minWidth: 0 }}><StyleInput value={value || ''} onChange={onChange} placeholder="0 4px 12px rgba(0,0,0,0.1)" /></Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ── DimensionControl ──────────────────────────────────────────────────────────
export function DimensionControl({ style, onChange }) {
  const [wOpen, setWOpen] = useState(false);
  const [hOpen, setHOpen] = useState(false);

  const row = (label, key, open, setOpen, maxKey, maxLabel) => (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: open ? 0.5 : 0 }}>
        <Box onClick={() => setOpen(v => !v)} sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: open ? PRIMARY : '#9ea8b3', flexShrink: 0, transition: 'color 0.1s' }}>
          {open ? <KeyboardArrowDownOutlined sx={{ fontSize: 14 }} /> : <KeyboardArrowRightOutlined sx={{ fontSize: 14 }} />}
        </Box>
        <Typography sx={{ fontSize: '0.7rem', color: '#7a8494', width: 48, flexShrink: 0 }}>{label}</Typography>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <UnitInput value={style[key] || ''} onChange={v => onChange(key, v)} placeholder="—" />
        </Box>
      </Box>
      {open && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 2.5, mb: 0.5 }}>
          <Typography sx={{ fontSize: '0.7rem', color: '#7a8494', width: 48, flexShrink: 0 }}>{maxLabel}</Typography>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <UnitInput value={style[maxKey] || ''} onChange={v => onChange(maxKey, v)} placeholder="—" />
          </Box>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {row('Width',  'width',  wOpen, setWOpen, 'maxWidth',  'Max W')}
      {row('Height', 'height', hOpen, setHOpen, 'maxHeight', 'Max H')}
    </Box>
  );
}